import { useI18n } from "vue-i18n";
import { formatDate, formatDateTime } from "@blpde/nx-ui";
import type { MasterNotification, NotificationAction } from "@/notifications";
import { appMessages, type Translator } from "@/i18n/appI18n";
import { apps } from "@/integration";

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_INSTANT =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/**
 * A producer sends a calendar day as `YYYY-MM-DD` and an instant as RFC 3339
 * (formatting belongs to the reader); the texts show them in the UI's format,
 * „02.11.2026“ and „02.11.2026, 14:00“. An all-day event's start names no time
 * of day: its text shows the event's own day (`date`).
 */
function withLocalDates(
  data: Record<string, unknown>,
  locale: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (typeof value !== "string") {
      out[key] = value;
    } else if (ISO_DAY.test(value)) {
      out[key] = formatDate(value, { locale }) || value;
    } else if (ISO_INSTANT.test(value)) {
      out[key] =
        data.allDay === true && typeof data.date === "string"
          ? formatDate(data.date, { locale }) || value
          : formatDateTime(value, { locale }) || value;
    } else {
      out[key] = value;
    }
  }
  return out;
}

/**
 * How a notification reads: its title, body, module and actions. Shared by
 * the bell's panel and the notifications page, so a row says the same in
 * both.
 */
export function useNotificationText() {
  const { t, te, locale } = useI18n();

  function notificationParams(n: MasterNotification): Record<string, unknown> {
    const data =
      n.data && typeof n.data === "object" && !Array.isArray(n.data)
        ? withLocalDates(n.data as Record<string, unknown>, String(locale.value))
        : {};
    // Prefer localized type+id when we know the resource; fall back to backend label.
    const resourceLabel =
      formatResourceFallback(data) ||
      (typeof data.resourceLabel === "string" && data.resourceLabel) ||
      "";
    const actorName =
      (typeof data.actorName === "string" && data.actorName.trim()) ||
      t("connect.notifications.untitled");
    // `count` is the name a text with plural forms reads („3 neue
    // Nachrichten“); `groupCount` the one older keys use.
    const groupCount = groupCountOf(n);
    return {
      ...data,
      groupCount,
      count: groupCount,
      resourceLabel,
      actorName,
    };
  }

  /** How many events the notification folds: 1 for a single one. */
  function groupCountOf(n: MasterNotification): number {
    const count = Number(n.groupCount ?? 1);
    return Number.isFinite(count) && count > 0 ? count : 1;
  }

  /** Localize common backend labels like "Correspondence 12". */
  function formatResourceFallback(data: Record<string, unknown>): string {
    const type = String(
      data.resourceType || data.recordTable || "",
    ).toLowerCase();
    const id = data.resourceID ?? data.recordId;
    if (!type || id == null || id === "") return "";
    const i18nKey = `connect.mentions.resources.${type}`;
    const label = te(i18nKey) ? t(i18nKey) : type.replace(/_/g, " ");
    return `${label} ${id}`;
  }

  /**
   * A notification's keys belong to the module that sent it (module key = app
   * key), so they resolve in that app's own table (I-2284); keys of modules
   * without an app of their own resolve in Connect's.
   */
  function messagesOf(n: MasterNotification): Translator {
    const app = apps.find((entry) => entry.id === n.moduleKey);
    return app
      ? appMessages(app.id)
      : {
          t: (key, named, plural) =>
            plural === undefined
              ? t(key, named ?? {})
              : t(key, named ?? {}, plural),
          te: (key) => te(key),
        };
  }

  function renderText(n: MasterNotification, key: "title" | "body"): string {
    const i18nKey = key === "title" ? n.titleKey : n.bodyKey;
    const raw = key === "title" ? n.title : n.body;
    const messages = messagesOf(n);
    // The group count picks the form of a text that has a plural one
    // („{title} | {title} ({count})“).
    if (i18nKey && messages.te(i18nKey))
      return messages.t(i18nKey, notificationParams(n), groupCountOf(n));
    return (raw || "").trim();
  }

  function titleOf(n: MasterNotification): string {
    return renderText(n, "title") || t("connect.notifications.untitled");
  }

  function bodyOf(n: MasterNotification): string {
    return renderText(n, "body");
  }

  /** The sending module's name („ERP“, „Kalender“); its key upper-cased without one. */
  function moduleLabel(key?: string): string {
    if (!key) return "";
    const i18nKey = `connect.mentions.modules.${key.toLowerCase()}`;
    return te(i18nKey) ? t(i18nKey) : key.toUpperCase();
  }

  /**
   * An action's caption: its key in the sending app's table (like the
   * title's), else the English fallback master stores, else its key.
   */
  function actionLabel(n: MasterNotification, action: NotificationAction): string {
    const messages = messagesOf(n);
    if (action.labelKey && messages.te(action.labelKey))
      return messages.t(action.labelKey, notificationParams(n));
    return (action.label || "").trim() || action.key;
  }

  /**
   * What an action reads as once taken („Freigegeben“ for „Freigeben“): its
   * resolved key in the sending app's table, else the resolved English
   * fallback, else the action's own caption.
   */
  function resolvedLabelOf(
    n: MasterNotification,
    action: NotificationAction,
  ): string {
    const messages = messagesOf(n);
    if (action.resolvedLabelKey && messages.te(action.resolvedLabelKey))
      return messages.t(action.resolvedLabelKey, notificationParams(n));
    return (action.resolvedLabel || "").trim() || actionLabel(n, action);
  }

  /**
   * How the action a notification was dealt with through reads; empty when
   * it is not resolved or was resolved by none of its own actions.
   */
  function resolvedActionLabel(n: MasterNotification): string {
    if (!n.resolvedAt || !n.resolvedActionKey) return "";
    const action = (n.actions ?? []).find(
      (item) => item?.key === n.resolvedActionKey,
    );
    return action ? resolvedLabelOf(n, action) : "";
  }

  return {
    titleOf,
    bodyOf,
    moduleLabel,
    actionLabel,
    resolvedLabelOf,
    resolvedActionLabel,
  };
}
